import routesPaths from 'constants/routesPaths';
import { EVENT_REDIRECTION_URL } from 'utils/constants';

const DOWNLOAD_APP_ID_PATTERN = /^[0-9a-zA-Z-]+$/;

export const ALLOWED_DOWNLOAD_APP_QUERY_KEYS = new Set(['eventId', 'pageId']);

const getDownloadAppPath = () => `/${EVENT_REDIRECTION_URL}`;

const normalizeDownloadAppSearch = (search = '') => {
    const trimmed = String(search).trim();
    if (!trimmed) {
        return '';
    }
    if (trimmed.startsWith('?')) {
        return `?${trimmed.replace(/^\?+/, '')}`;
    }
    return trimmed;
};

const readSearchParams = (search = '', href = '') => {
    const normalizedSearch = normalizeDownloadAppSearch(search);
    if (normalizedSearch) {
        return new URLSearchParams(normalizedSearch);
    }
    if (href) {
        try {
            const parsedHref = new URL(href);
            const normalizedHrefSearch = normalizeDownloadAppSearch(
                parsedHref.search
            );
            if (normalizedHrefSearch) {
                return new URLSearchParams(normalizedHrefSearch);
            }
        } catch (e) {
            // ignore invalid href
        }
    }
    return new URLSearchParams();
};

export const isValidDownloadAppId = (value) => {
    if (value === null || value === undefined) {
        return false;
    }
    const id = String(value).trim();
    return id.length > 0 && DOWNLOAD_APP_ID_PATTERN.test(id);
};

/**
 * Reads eventId/pageId from /download-app query string.
 * Returns { eventId, pageId, hasEventQueryParams, hasInvalidEventParams }.
 */
export const parseDownloadAppEventParams = (search = '', href = '') => {
    const params = readSearchParams(search, href);
    const hasEventIdParam = params.has('eventId');
    const hasPageIdParam = params.has('pageId');
    const hasEventQueryParams = hasEventIdParam || hasPageIdParam;
    const rawEventId = hasEventIdParam ? params.get('eventId') : '';
    const rawPageId = hasPageIdParam ? params.get('pageId') : '';

    const eventIdValid = isValidDownloadAppId(rawEventId);
    const pageIdValid = isValidDownloadAppId(rawPageId);
    const hasValidPair = eventIdValid && pageIdValid;

    const hasInvalidEventParams =
        hasEventQueryParams &&
        (!hasValidPair ||
            (hasEventIdParam && !eventIdValid) ||
            (hasPageIdParam && !pageIdValid));

    return {
        eventId: hasValidPair ? String(rawEventId).trim() : '',
        pageId: hasValidPair ? String(rawPageId).trim() : '',
        hasEventContext: hasValidPair,
        hasInvalidEventParams,
    };
};

/**
 * True when the query string contains keys other than eventId/pageId.
 */
export const hasExtraneousDownloadAppQueryParams = (search = '', href = '') => {
    const params = readSearchParams(search, href);
    const keys = [...params.keys()];
    return keys.some((key) => !ALLOWED_DOWNLOAD_APP_QUERY_KEYS.has(key));
};

/**
 * Canonical path + query for /download-app (no origin).
 */
export const getCanonicalDownloadAppRelativeUrl = (search = '', href = '') => {
    const { eventId, pageId, hasEventContext } = parseDownloadAppEventParams(
        search,
        href
    );
    const base = getDownloadAppPath();
    if (hasEventContext) {
        const params = new URLSearchParams({
            eventId,
            pageId,
        });
        return `${base}?${params.toString()}`;
    }
    return base;
};

const normalizeRelativeUrlForCompare = (pathname = '', search = '') => {
    const normalizedSearch = normalizeDownloadAppSearch(search);
    return `${pathname}${normalizedSearch}`;
};

/**
 * True when the address bar should be replaced with the canonical download-app URL.
 */
export const shouldSanitizeDownloadAppUrl = (
    pathname = '',
    search = '',
    href = ''
) => {
    const downloadAppPath = getDownloadAppPath();
    if (pathname !== downloadAppPath) {
        return false;
    }
    const current = normalizeRelativeUrlForCompare(pathname, search);
    const canonical = getCanonicalDownloadAppRelativeUrl(search, href);
    return current !== canonical;
};

export const buildParticipantEventPath = (eventId, pageId) =>
    routesPaths.PARTICIPANT_USER.EVENT_DETIAL_PAGE
        .replace(':eventId', eventId)
        .replace(':pageId', pageId);

export const buildDownloadAppQrUrl = (
    origin = typeof window !== 'undefined' ? window.location.origin : '',
    { eventId = '', pageId = '' } = {}
) => {
    if (!origin) {
        return `/${EVENT_REDIRECTION_URL}`;
    }
    const base = `${origin}/${EVENT_REDIRECTION_URL}`;
    if (isValidDownloadAppId(eventId) && isValidDownloadAppId(pageId)) {
        const params = new URLSearchParams({
            eventId: String(eventId).trim(),
            pageId: String(pageId).trim(),
        });
        return `${base}?${params.toString()}`;
    }
    return base;
};
